import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import {
  BASE_SUBCATEGORIES,
  PRICING_CONFIG,
  PRICING_GROUPS,
  type PricingConfig,
  type PricingGroup,
  filterHiddenKeys,
  formatLabel,
  getBaseSubcategoryData,
  getUncategorizedBases,
} from 'element-domain'
import { useCallback, useEffect, useState } from 'react'

import { textValueComponent } from '@salsita-ai/platform-project-sdk/domain'

import { adminInterface } from '../../systems/admin-interface'

function parsePricing(data: string): PricingConfig {
  try {
    return data ? ({ ...PRICING_CONFIG, ...JSON.parse(data) } as PricingConfig) : PRICING_CONFIG
  } catch {
    return PRICING_CONFIG
  }
}

export const PricingPageElement = () => {
  const [activeTab, setActiveTab] = useState(0)
  const { storeSystem } = adminInterface.useDomain()

  const pricingEntity = adminInterface.useStoreSelector(emission => {
    const entity = emission.state.entities.find(e => e.type === 'PricingSettings')
    if (!entity || !('data' in entity)) return null
    return { id: entity.id, data: (entity as { readonly data: string }).data }
  }, [])

  const [pricing, setPricing] = useState<PricingConfig>(() => parsePricing(pricingEntity?.data ?? ''))

  useEffect(() => {
    setPricing(parsePricing(pricingEntity?.data ?? ''))
  }, [pricingEntity?.data])

  const save = useCallback(() => {
    if (!pricingEntity) return
    storeSystem.stageAndCommit([
      adminInterface.updates.ComponentModification({
        componentInstance: {
          entityId: pricingEntity.id as Parameters<
            typeof adminInterface.updates.ComponentModification
          >[0]['componentInstance']['entityId'],
          componentType: textValueComponent,
          instanceName: 'data',
        },
        writableState: { value: JSON.stringify(pricing) },
        updateTag: 'pricing-settings-update',
      }),
    ])
  }, [storeSystem, pricingEntity, pricing])

  const resetToDefaults = useCallback(() => {
    setPricing(PRICING_CONFIG)
  }, [])

  const updateTopField = useCallback(
    (topKey: string, field: 'basePrice' | 'compoundingRate' | 'pricingStep', value: number) => {
      setPricing(prev => {
        const currentTop = prev.tops[topKey] ?? { basePrice: 0, compoundingRate: 0.05, pricingStep: 6 }
        return { ...prev, tops: { ...prev.tops, [topKey]: { ...currentTop, [field]: value } } } as PricingConfig
      })
    },
    []
  )

  const updateSimpleField = useCallback((category: string, key: string, value: number) => {
    setPricing(prev => {
      const current = prev[category as keyof PricingConfig]
      if (typeof current === 'object' && current !== null) {
        return { ...prev, [category]: { ...(current as Record<string, number>), [key]: value } } as PricingConfig
      }
      return prev
    })
  }, [])

  const updateScalarField = useCallback((field: 'grooves' | 'cornerRadius' | 'hasCrossbrace', value: number) => {
    setPricing(prev => ({ ...prev, [field]: value }) as PricingConfig)
  }, [])

  const setAllBasesInCategory = useCallback((subcategory: string, value: number) => {
    const keys = BASE_SUBCATEGORIES[subcategory]
    if (!keys) return
    setPricing(prev => {
      const updated = { ...(prev.bases as Record<string, number>) }
      for (const key of keys) {
        if (key in updated) updated[key] = value
      }
      return { ...prev, bases: updated } as PricingConfig
    })
  }, [])

  if (!pricingEntity) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No pricing configuration found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click &quot;Initialize Pricing&quot; to create the default pricing configuration.
        </Typography>
      </Box>
    )
  }

  const group = PRICING_GROUPS[activeTab]

  return (
    <Box sx={{ overflow: 'auto', maxHeight: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_: React.SyntheticEvent, v: number) => {
            setActiveTab(v)
          }}
        >
          {PRICING_GROUPS.map(g => (
            <Tab key={g.label} label={g.label} />
          ))}
        </Tabs>

        <Stack direction="row" gap={1}>
          <Box
            component="button"
            onClick={resetToDefaults}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              bgcolor: 'background.paper',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            Reset Defaults
          </Box>
          <Box
            component="button"
            onClick={save}
            sx={{
              border: 'none',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            Save
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ p: 2 }}>
        {group && (
          <GroupEditor
            group={group}
            pricing={pricing}
            onUpdateTop={updateTopField}
            onUpdateSimple={updateSimpleField}
            onUpdateScalar={updateScalarField}
            onSetAllBasesInCategory={setAllBasesInCategory}
          />
        )}
      </Box>
    </Box>
  )
}

function GroupEditor({
  group,
  pricing,
  onUpdateTop,
  onUpdateSimple,
  onUpdateScalar,
  onSetAllBasesInCategory,
}: {
  readonly group: PricingGroup
  readonly pricing: PricingConfig
  readonly onUpdateTop: (topKey: string, field: 'basePrice' | 'compoundingRate' | 'pricingStep', value: number) => void
  readonly onUpdateSimple: (category: string, key: string, value: number) => void
  readonly onUpdateScalar: (field: 'grooves' | 'cornerRadius' | 'hasCrossbrace', value: number) => void
  readonly onSetAllBasesInCategory: (subcategory: string, value: number) => void
}) {
  return (
    <Stack gap={1}>
      {group.sections.map(section => {
        if (section.type === 'tops') {
          return <TopsPricingEditor key={section.category} tops={pricing[section.category]} onUpdate={onUpdateTop} />
        }

        if (section.type === 'scalar') {
          const value = pricing[section.category] as number
          return (
            <Accordion key={section.category}>
              <AccordionSummary>
                <Typography variant="subtitle2">{section.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <NumberField
                  label={section.label}
                  value={value}
                  onChange={v => {
                    onUpdateScalar(section.category as 'grooves' | 'cornerRadius' | 'hasCrossbrace', v)
                  }}
                />
              </AccordionDetails>
            </Accordion>
          )
        }

        const rawData = pricing[section.category] as Record<string, number>

        if (section.category === 'bases') {
          return (
            <BasesEditor
              key="bases"
              data={rawData}
              onUpdate={(key, value) => {
                onUpdateSimple('bases', key, value)
              }}
              onSetAllInCategory={onSetAllBasesInCategory}
            />
          )
        }

        const data = filterHiddenKeys(rawData, section.category)

        return (
          <SimplePricingEditor
            key={section.category}
            title={section.label}
            category={section.category}
            data={data}
            onUpdate={onUpdateSimple}
            percentDisplay={section.category === 'woodSpecies'}
          />
        )
      })}
    </Stack>
  )
}

function SetAllRow({
  subcategory,
  onSetAll,
}: {
  readonly subcategory: string
  readonly onSetAll: (subcategory: string, value: number) => void
}) {
  const [value, setValue] = useState('')

  return (
    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
      <TextField
        label={`Set all ${subcategory}`}
        type="number"
        size="small"
        value={value}
        onChange={e => {
          setValue(e.target.value)
        }}
        sx={{ flex: 1 }}
      />
      <Button
        variant="contained"
        size="small"
        disabled={value === ''}
        onClick={() => {
          onSetAll(subcategory, Number(value))
          setValue('')
        }}
        sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 2, py: '7px' }}
      >
        Set All
      </Button>
    </Stack>
  )
}

function BasesEditor({
  data,
  onUpdate,
  onSetAllInCategory,
}: {
  readonly data: Record<string, number>
  readonly onUpdate: (key: string, value: number) => void
  readonly onSetAllInCategory: (subcategory: string, value: number) => void
}) {
  const uncategorized = getUncategorizedBases(data)

  return (
    <>
      {Object.entries(BASE_SUBCATEGORIES).map(([subcategory]) => {
        const subData = getBaseSubcategoryData(data, subcategory)
        if (Object.keys(subData).length === 0) return null
        return (
          <Accordion key={subcategory}>
            <AccordionSummary>
              <Typography variant="subtitle2">{subcategory}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SetAllRow subcategory={subcategory} onSetAll={onSetAllInCategory} />
              <Stack gap={1}>
                {Object.entries(subData).map(([key, value]) => (
                  <NumberField
                    key={key}
                    label={formatLabel(key, 'bases')}
                    value={value}
                    onChange={v => {
                      onUpdate(key, v)
                    }}
                  />
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )
      })}

      {Object.keys(uncategorized).length > 0 && (
        <Accordion>
          <AccordionSummary>
            <Typography variant="subtitle2">Other</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={1}>
              {Object.entries(uncategorized).map(([key, value]) => (
                <NumberField
                  key={key}
                  label={formatLabel(key, 'bases')}
                  value={value}
                  onChange={v => {
                    onUpdate(key, v)
                  }}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  )
}

function TopsPricingEditor({
  tops,
  onUpdate,
}: {
  readonly tops: Record<string, { basePrice: number; compoundingRate: number; pricingStep: number }>
  readonly onUpdate: (topKey: string, field: 'basePrice' | 'compoundingRate' | 'pricingStep', value: number) => void
}) {
  return (
    <Accordion>
      <AccordionSummary>
        <Typography variant="subtitle2">Top Shapes</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={2}>
          {Object.entries(tops).map(([key, val]) => (
            <Box key={key}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                {formatLabel(key, 'tops')}
              </Typography>
              <Stack direction="row" gap={1}>
                <NumberField
                  label="Base Price"
                  value={val.basePrice}
                  onChange={v => {
                    onUpdate(key, 'basePrice', v)
                  }}
                />
                <NumberField
                  label="Rate %"
                  value={Math.round(val.compoundingRate * 100)}
                  onChange={v => {
                    onUpdate(key, 'compoundingRate', v / 100)
                  }}
                />
                <NumberField
                  label="Step (inches)"
                  value={val.pricingStep}
                  onChange={v => {
                    onUpdate(key, 'pricingStep', v)
                  }}
                />
              </Stack>
            </Box>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function SimplePricingEditor({
  title,
  category,
  data,
  onUpdate,
  percentDisplay,
}: {
  readonly title: string
  readonly category: string
  readonly data: Record<string, number>
  readonly onUpdate: (category: string, key: string, value: number) => void
  readonly percentDisplay?: boolean
}) {
  return (
    <Accordion>
      <AccordionSummary>
        <Typography variant="subtitle2">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap={1}>
          {Object.entries(data).map(([key, value]) => {
            const label = formatLabel(key, category)
            return (
              <NumberField
                key={key}
                label={percentDisplay ? `${label} %` : label}
                value={percentDisplay ? Math.round(value * 100) : value}
                onChange={v => {
                  onUpdate(category, key, percentDisplay ? v / 100 : v)
                }}
              />
            )
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}

function NumberField({
  label,
  value,
  onChange,
  step = 1,
}: {
  readonly label: string
  readonly value: number
  readonly onChange: (value: number) => void
  readonly step?: number
}) {
  return (
    <TextField
      label={label}
      type="number"
      size="small"
      value={value}
      onChange={e => {
        onChange(Number(e.target.value))
      }}
      inputProps={{ step }}
      fullWidth
    />
  )
}
